/**
 * Component Engine — reusable design components.
 *
 * A component definition knows how to expand itself into engine commands
 * given a rect and the resolved design tokens (palette roles, typography
 * roles, spacing). Components never position themselves — the composition
 * engine hands them a rect that the alignment engine already settled.
 *
 * build(rect, tokens, props) → array of { command, params } descriptors
 * (raw descriptors, not validated command objects; the composition engine
 * pushes them through the command engine).
 */

const componentRegistry = new Map();

export const registerComponent = (def) => {
  if (!def?.id || typeof def.build !== 'function') {
    throw new Error('registerComponent: definition needs an id and a build()');
  }
  componentRegistry.set(def.id, def);
  return def.id;
};

export const getComponent = (id) => componentRegistry.get(id) || null;
export const listComponents = () =>
  [...componentRegistry.values()].map(({ id, name, category }) => ({ id, name, category }));

/** Expand a component into command descriptors. */
export const buildComponent = (id, rect, tokens, props = {}) => {
  const def = componentRegistry.get(id);
  if (!def) throw new Error(`Unknown component "${id}"`);
  return def.build(rect, tokens, props);
};

// ── Built-in components ──────────────────────────────────────────────────────
// tokens = { palette: roles, typography: roles, spacing }

const BUILT_INS = [
  {
    id: 'button',
    name: 'Button',
    category: 'interactive',
    build: (rect, { palette, typography }, { text = 'Learn More' }) => [{
      command: 'createCTA',
      params: {
        ...rect, text,
        ...typography.cta,
        color: palette.text === '#111111' ? '#ffffff' : '#111111',
        background: palette.primary,
        borderRadius: Math.round(rect.height / 2),
      },
    }],
  },
  {
    id: 'card',
    name: 'Card',
    category: 'container',
    build: (rect, { palette, spacing }, { title = '', body = '' }, ) => [
      { command: 'createShape', params: { ...rect, shape: 'rounded-rect', fill: palette.neutral, opacity: 1 } },
      { command: 'createSubheadline', params: {
        x: rect.x + spacing.scale.md, y: rect.y + spacing.scale.md,
        width: rect.width - spacing.scale.md * 2, height: Math.round(rect.height * 0.3),
        text: title, color: palette.text, align: 'left',
      } },
      { command: 'createParagraph', params: {
        x: rect.x + spacing.scale.md, y: rect.y + Math.round(rect.height * 0.35),
        width: rect.width - spacing.scale.md * 2, height: Math.round(rect.height * 0.55),
        text: body, color: palette.text, align: 'left',
      } },
    ],
  },
  {
    id: 'badge',
    name: 'Badge',
    category: 'emphasis',
    build: (rect, { palette, typography }, { text = 'NEW' }) => [{
      command: 'createBadge',
      params: {
        ...rect, text,
        ...typography.caption, fontWeight: 700,
        color: '#ffffff', background: palette.accent,
        borderRadius: Math.round(rect.height / 2),
      },
    }],
  },
  {
    id: 'priceTag',
    name: 'Price Tag',
    category: 'emphasis',
    build: (rect, { palette, typography }, { price = '$0', label = '' }) => [
      { command: 'createShape', params: { ...rect, shape: 'circle', fill: palette.accent } },
      { command: 'createHeadline', params: {
        x: rect.x, y: rect.y + Math.round(rect.height * 0.28),
        width: rect.width, height: Math.round(rect.height * 0.4),
        text: label ? `${price} ${label}` : price,
        ...typography.headline,
        fontSize: Math.round(Math.min(rect.width, rect.height) * 0.28),
        color: '#ffffff', align: 'center',
      } },
    ],
  },
  {
    id: 'blob',
    name: 'Organic Blob',
    category: 'decorative',
    build: (rect, { palette }, { seed = 1, opacity = 0.35, role = 'secondary' }) => [{
      command: 'createBlob',
      params: { ...rect, fill: palette[role] || palette.secondary, seed, opacity },
    }],
  },
  {
    id: 'wave',
    name: 'Wave',
    category: 'decorative',
    build: (rect, { palette }, { direction = 'bottom', role = 'primary' }) => [{
      command: 'createWave',
      params: { ...rect, fill: palette[role] || palette.primary, amplitude: Math.round(rect.height / 3), direction },
    }],
  },
  {
    id: 'frame',
    name: 'Frame',
    category: 'media',
    build: (rect, tokens, { label = 'Image' }) => [{
      command: 'createImagePlaceholder',
      params: { ...rect, fit: 'cover', label },
    }],
  },
  {
    id: 'mask',
    name: 'Masked Image',
    category: 'media',
    build: (rect, { palette }, { label = 'Image', shape = 'circle' }) => [
      { command: 'createShape', params: { ...rect, shape, fill: palette.neutral } },
      { command: 'createImagePlaceholder', params: { ...rect, fit: 'cover', label: `${label} (${shape} mask)` } },
    ],
  },
  {
    id: 'qr',
    name: 'QR Code',
    category: 'utility',
    build: (rect) => [{
      command: 'createImagePlaceholder',
      params: { ...rect, fit: 'contain', label: 'QR code' },
    }],
  },
  {
    id: 'socialIcons',
    name: 'Social Icons',
    category: 'utility',
    build: (rect, { palette }, { networks = ['instagram', 'facebook', 'x'] }) => {
      const size = Math.min(rect.height, Math.floor(rect.width / networks.length));
      const gap = networks.length > 1
        ? Math.floor((rect.width - size * networks.length) / (networks.length - 1))
        : 0;
      return networks.map((network, i) => ({
        command: 'createShape',
        params: {
          x: rect.x + i * (size + gap), y: rect.y,
          width: size, height: size,
          shape: `icon-${network}`, fill: palette.text,
        },
      }));
    },
  },
  {
    id: 'testimonial',
    name: 'Testimonial',
    category: 'content',
    build: (rect, { palette, spacing }, { quote = '', author = '' }) => [
      { command: 'createShape', params: { ...rect, shape: 'rounded-rect', fill: palette.neutral } },
      { command: 'createParagraph', params: {
        x: rect.x + spacing.scale.md, y: rect.y + spacing.scale.md,
        width: rect.width - spacing.scale.md * 2, height: Math.round(rect.height * 0.6),
        text: `“${quote}”`, color: palette.text, align: 'left',
      } },
      { command: 'createParagraph', params: {
        x: rect.x + spacing.scale.md, y: rect.y + Math.round(rect.height * 0.7),
        width: rect.width - spacing.scale.md * 2, height: Math.round(rect.height * 0.2),
        text: `— ${author}`, color: palette.text, align: 'left',
      } },
    ],
  },
  {
    id: 'featureCard',
    name: 'Feature Card',
    category: 'content',
    build: (rect, tokens, props) =>
      componentRegistry.get('card').build(rect, tokens, props),
  },
  {
    id: 'decorativeShape',
    name: 'Decorative Shape',
    category: 'decorative',
    build: (rect, { palette }, { shape = 'circle', role = 'accent', opacity = 0.3, rotation = 0 }) => [{
      command: 'createShape',
      params: { ...rect, shape, fill: palette[role] || palette.accent, opacity, rotation },
    }],
  },
];

BUILT_INS.forEach(registerComponent);

export const BUILT_IN_COMPONENT_IDS = BUILT_INS.map((c) => c.id);
